/**
 * Flashcard Preview Editor - Xem preview và chỉnh sửa flashcards trước khi lưu
 * Giống như NotebookLM style
 */

import React, { useState } from 'react'
import { Edit2, Save, X, Trash2, RotateCcw, ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import KaTeXRenderer from '../common/KaTeXRenderer'
import type { FlashcardDraft } from '../../types/flashcard'

interface FlashcardPreviewEditorProps {
  drafts: FlashcardDraft[]
  onUpdate: (updatedDrafts: FlashcardDraft[]) => void
  onConfirm: () => void
  onCancel?: () => void
  isLoading?: boolean
  confirmButtonText?: string
}

const FlashcardPreviewEditor: React.FC<FlashcardPreviewEditorProps> = ({
  drafts,
  onUpdate,
  onConfirm,
  onCancel,
  isLoading = false,
  confirmButtonText = 'Lưu Flashcards'
}) => {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<{ front: string; back: string; difficulty: 'easy' | 'medium' | 'hard' }>({
    front: '',
    back: '',
    difficulty: 'easy'
  })
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())

  // Start editing a card
  const startEdit = (draft: FlashcardDraft) => {
    setEditingId(draft.id)
    setEditForm({
      front: draft.front,
      back: draft.back,
      difficulty: draft.difficulty
    })
  }

  // Save edited card
  const saveEdit = () => {
    if (!editingId) return

    const updatedDrafts = drafts.map(draft =>
      draft.id === editingId
        ? {
            ...draft,
            front: editForm.front,
            back: editForm.back,
            difficulty: editForm.difficulty,
            isEdited: true
          }
        : draft
    )

    onUpdate(updatedDrafts)
    setEditingId(null)
  }

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({ front: '', back: '', difficulty: 'easy' })
  }

  // Delete a card
  const deleteCard = (id: string) => {
    if (window.confirm('Bạn có chắc muốn xóa flashcard này?')) {
      const updatedDrafts = drafts.filter(draft => draft.id !== id)
      onUpdate(updatedDrafts)
    }
  }

  // Toggle card expansion
  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedCards)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedCards(newExpanded)
  }

  // Expand all cards
  const expandAll = () => {
    setExpandedCards(new Set(drafts.map(d => d.id)))
  }

  // Collapse all cards
  const collapseAll = () => {
    setExpandedCards(new Set())
  }

  // Get difficulty label
  const getDifficultyLabel = (difficulty: 'easy' | 'medium' | 'hard'): string => {
    switch (difficulty) {
      case 'easy': return 'Dễ'
      case 'medium': return 'Trung bình'
      case 'hard': return 'Khó'
    }
  }

  // Get difficulty color
  const getDifficultyColor = (difficulty: 'easy' | 'medium' | 'hard'): string => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-700'
      case 'medium': return 'bg-yellow-100 text-yellow-700'
      case 'hard': return 'bg-red-100 text-red-700'
    }
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <Sparkles className="w-6 h-6 mr-2 text-purple-600" />
              Xem trước Flashcards
            </h2>
            <p className="text-gray-600 mt-1">
              AI đã tạo {drafts.length} flashcards. Bạn có thể chỉnh sửa trước khi lưu.
            </p>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={expandAll}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Mở tất cả
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Thu gọn
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span className="text-gray-700">
              Dễ: {drafts.filter(d => d.difficulty === 'easy').length}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
            <span className="text-gray-700">
              Trung bình: {drafts.filter(d => d.difficulty === 'medium').length}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            <span className="text-gray-700">
              Khó: {drafts.filter(d => d.difficulty === 'hard').length}
            </span>
          </div>
          <div className="flex items-center space-x-2 ml-auto">
            <span className="text-gray-700">
              Đã chỉnh sửa: {drafts.filter(d => d.isEdited).length}
            </span>
          </div>
        </div>
      </div>

      {/* Flashcard List */}
      <div className="space-y-3 mb-6">
        {drafts.map((draft, index) => {
          const isExpanded = expandedCards.has(draft.id)
          const isEditing = editingId === draft.id

          return (
            <div
              key={draft.id}
              className={`
                border-2 rounded-xl transition-all
                ${draft.isEdited ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}
                ${isExpanded ? 'shadow-lg' : 'shadow-sm'}
              `}
            >
              {/* Card Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => !isEditing && toggleExpand(draft.id)}
              >
                <div className="flex items-center space-x-3 flex-1">
                  <span className="text-sm font-bold text-gray-500">#{index + 1}</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${getDifficultyColor(draft.difficulty)}`}>
                    {getDifficultyLabel(draft.difficulty)}
                  </span>
                  <p className="font-semibold text-gray-800 truncate flex-1">
                    {draft.front}
                  </p>
                  {draft.isEdited && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                      Đã sửa
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  {!isEditing && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!isExpanded) {
                            toggleExpand(draft.id)
                          }
                          startEdit(draft)
                        }}
                        className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Chỉnh sửa"
                      >
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteCard(draft.id)
                        }}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                        title="Xóa"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleExpand(draft.id)
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title={isExpanded ? "Thu gọn" : "Mở rộng"}
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Card Content (Expanded) */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-200">
                  {isEditing ? (
                    // Edit Mode
                    <div className="space-y-4 pt-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Mặt trước (Câu hỏi/Khái niệm)
                        </label>
                        <textarea
                          value={editForm.front}
                          onChange={(e) => setEditForm({ ...editForm, front: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={2}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Mặt sau (Câu trả lời)
                        </label>
                        <textarea
                          value={editForm.back}
                          onChange={(e) => setEditForm({ ...editForm, back: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={4}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Độ khó
                        </label>
                        <select
                          value={editForm.difficulty}
                          onChange={(e) => setEditForm({ ...editForm, difficulty: e.target.value as 'easy' | 'medium' | 'hard' })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="easy">Dễ</option>
                          <option value="medium">Trung bình</option>
                          <option value="hard">Khó</option>
                        </select>
                      </div>

                      <div className="flex justify-end space-x-2 pt-2">
                        <button
                          onClick={cancelEdit}
                          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center space-x-2"
                        >
                          <X className="w-4 h-4" />
                          <span>Hủy</span>
                        </button>
                        <button
                          onClick={saveEdit}
                          className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                        >
                          <Save className="w-4 h-4" />
                          <span>Lưu</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode - Màu sắc chuyên nghiệp
                    <div className="space-y-4 pt-4">
                      {/* Front - Thiết kế chuyên nghiệp với viền đẹp */}
                      <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-3 border-amber-400 rounded-2xl p-6 shadow-lg relative overflow-hidden">
                        {/* Decorative elements */}
                        <div className="absolute top-3 right-3 w-6 h-6 bg-amber-200/50 rounded-full"></div>
                        <div className="absolute bottom-3 left-3 w-4 h-4 bg-yellow-200/50 rounded-full"></div>
                        
                        <h4 className="text-xs font-bold text-amber-800 mb-4 uppercase tracking-wide flex items-center relative z-10">
                          <span className="w-3 h-3 bg-amber-500 rounded-full mr-3 shadow-sm"></span>
                          Câu hỏi
                        </h4>
                        <div className="text-blue-900 relative z-10">
                          <KaTeXRenderer content={draft.front} className="text-lg font-bold leading-relaxed" />
                        </div>
                      </div>

                      {/* Back - Thiết kế chuyên nghiệp với viền đẹp */}
                      <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 border-3 border-blue-400 rounded-2xl p-6 shadow-lg relative overflow-hidden">
                        {/* Decorative elements */}
                        <div className="absolute top-3 right-3 w-6 h-6 bg-blue-200/50 rounded-full"></div>
                        <div className="absolute bottom-3 left-3 w-4 h-4 bg-cyan-200/50 rounded-full"></div>
                        
                        <h4 className="text-xs font-bold text-blue-800 mb-4 uppercase tracking-wide flex items-center relative z-10">
                          <span className="w-3 h-3 bg-blue-500 rounded-full mr-3 shadow-sm"></span>
                          Câu trả lời
                        </h4>
                        <div className="text-blue-900 relative z-10">
                          <KaTeXRenderer content={draft.back} className="text-lg font-medium leading-relaxed whitespace-pre-wrap" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center p-6 bg-gray-50 rounded-xl border border-gray-200">
        <button
          onClick={onCancel}
          className="px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 font-semibold transition-colors"
          disabled={isLoading}
        >
          Hủy bỏ
        </button>

        <button
          onClick={onConfirm}
          disabled={isLoading || drafts.length === 0}
          className="px-8 py-3 text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl hover:from-blue-700 hover:to-purple-700 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-5 h-5" />
          <span>{confirmButtonText || `Lưu ${drafts.length} Flashcards`}</span>
        </button>
      </div>
    </div>
  )
}

export default FlashcardPreviewEditor

